import * as XLSX from "xlsx";
import { AppData, Fach, FachStufeConfig } from "../types";
import { calculateLehrpersonStats } from "./calculations";
import { generateId } from "./storage";
import { calculateEntschaedigung } from "./calculations";

/**
 * Export all application data to an Excel file with multiple sheets.
 */
export const exportToExcel = (data: AppData) => {
  const wb = XLSX.utils.book_new();

  // 1. Lehrpersonen sheet
  const lpRows = data.lehrpersonen.map((lp) => {
    const stats = calculateLehrpersonStats(lp.id, data);
    return {
      Name: lp.name,
      Fächer: lp.faecher.join(", "),
      "Vertr. Lektionen": lp.solllektionen,
      Geburtsdatum: lp.geburtsdatum || "",
      Altersentlastung: stats.hatAltersentlastung ? "Ja" : "Nein",
      "Eff. Soll": round(stats.soll),
      "Ist Std": round(stats.ist),
      Entschädigung: round(stats.entschaedigung),
      Differenz: round(stats.differenz),
      "Auslastung %": round(stats.prozent),
      Status: stats.status,
    };
  });
  const lpSheet = XLSX.utils.json_to_sheet(lpRows);
  XLSX.utils.book_append_sheet(wb, lpSheet, "Lehrpersonen");

  // 2. Klassen sheet
  const klRows = data.klassen.map((kl) => {
    const klassenlehrer = kl.klassenlehrerId
      ? data.lehrpersonen.find((lp) => lp.id === kl.klassenlehrerId)?.name || ""
      : "";
    const zuteilungen = data.zuteilungen.filter((zt) => zt.klasseId === kl.id);
    const totalLektionen = zuteilungen.reduce((sum, zt) => sum + zt.lektionen, 0);
    return {
      Klasse: kl.name,
      Stufe: kl.stufe,
      Profil: kl.profil,
      Bestand: kl.bestand,
      Semester: kl.semester,
      Schuljahr: kl.schuljahr,
      Klassenlehrer: klassenlehrer,
      Zuteilungen: zuteilungen.length,
      "Total Lektionen": totalLektionen,
    };
  });
  const klSheet = XLSX.utils.json_to_sheet(klRows);
  XLSX.utils.book_append_sheet(wb, klSheet, "Klassen");

  // 3. Fächer sheet
  const fachRows = data.faecher.map((fach) => {
    const zuteilungen = data.zuteilungen.filter((zt) => zt.fachId === fach.id);
    const totalLektionen = zuteilungen.reduce((sum, zt) => sum + zt.lektionen, 0);
    return {
      Kürzel: fach.kuerzel,
      Name: fach.name,
      Basislektionen: fach.basislektionen ?? "",
      "Basislektionen AE": fach.basislektionenAltersentlastung ?? "",
      Zuteilungen: zuteilungen.length,
      "Total Lektionen": totalLektionen,
    };
  });
  const fachSheet = XLSX.utils.json_to_sheet(fachRows);
  XLSX.utils.book_append_sheet(wb, fachSheet, "Fächer");

  // 4. Zuteilungen sheet
  const ztRows = data.zuteilungen.map((zt) => {
    const lp = zt.lpId ? data.lehrpersonen.find((l) => l.id === zt.lpId) : null;
    const kl = data.klassen.find((k) => k.id === zt.klasseId);
    const fach = data.faecher.find((f) => f.id === zt.fachId);
    return {
      Lehrperson: lp?.name || "(unbesetzt)",
      Klasse: kl?.name || "",
      Fach: fach ? `${fach.kuerzel} - ${fach.name}` : "",
      Typ: zt.typ,
      Lektionen: zt.lektionen,
      HK: zt.hatHk ? "Ja" : "Nein",
      Bemerkungen: zt.bemerkungen || "",
    };
  });
  const ztSheet = XLSX.utils.json_to_sheet(ztRows);
  XLSX.utils.book_append_sheet(wb, ztSheet, "Zuteilungen");

  // Generate and download
  XLSX.writeFile(wb, `Adjunkty_Export_${today()}.xlsx`);
};

const round = (n: number) => Math.round(n * 100) / 100;

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const defaultStufeConfig = (): FachStufeConfig => ({
  1: false, 2: false, 3: false, 4: false, 5: false, 6: false,
});

/**
 * Import application data from a previously exported Excel file.
 * Returns the parsed AppData or throws an error.
 */
export const importFromExcel = (file: File): Promise<AppData> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target?.result, { type: "array" });
        const data = parseWorkbook(wb);
        resolve(data);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error("Datei konnte nicht gelesen werden"));
    reader.readAsArrayBuffer(file);
  });
};

function parseWorkbook(wb: XLSX.WorkBook): AppData {
  // Parse Fächer first (needed to resolve references)
  const fachRows = readSheet(wb, "Fächer");
  const faecher: Fach[] = fachRows.map((row) => ({
    id: generateId("F"),
    kuerzel: String(row["Kürzel"] || ""),
    name: String(row["Name"] || ""),
    basislektionen: row["Basislektionen"] ? Number(row["Basislektionen"]) : undefined,
    basislektionenAltersentlastung: row["Basislektionen AE"] ? Number(row["Basislektionen AE"]) : undefined,
    pflichtfach: defaultStufeConfig(),
    freifach: defaultStufeConfig(),
    hk: defaultStufeConfig(),
  }));

  // Parse Lehrpersonen
  const lpRows = readSheet(wb, "Lehrpersonen");
  const lehrpersonen = lpRows.map((row) => {
    const geburtsdatum = parseDate(row["Geburtsdatum"]);
    return {
      id: generateId("LP"),
      name: String(row["Name"] || ""),
      faecher: String(row["Fächer"] || "")
        .split(",")
        .map((f) => f.trim())
        .filter((f) => f),
      solllektionen: Number(row["Vertr. Lektionen"] || 24),
      ...(geburtsdatum ? { geburtsdatum } : {}),
    };
  });

  // Parse Klassen
  const klRows = readSheet(wb, "Klassen");
  const klassen = klRows.map((row) => {
    const klassenlehrerName = String(row["Klassenlehrer"] || "");
    const klassenlehrer = klassenlehrerName
      ? lehrpersonen.find((lp) => lp.name === klassenlehrerName)
      : null;
    return {
      id: generateId("KL"),
      name: String(row["Klasse"] || ""),
      stufe: Number(row["Stufe"] || 1),
      profil: String(row["Profil"] || ""),
      bestand: Number(row["Bestand"] || 0),
      semester: (String(row["Semester"] || "FS") as "HS" | "FS"),
      schuljahr: Number(row["Schuljahr"] || 2025),
      klassenlehrerId: klassenlehrer?.id || null,
    };
  });

  // Parse Zuteilungen (resolve names back to IDs)
  const ztRows = readSheet(wb, "Zuteilungen");
  const zuteilungen = ztRows.map((row) => {
    const lpName = String(row["Lehrperson"] || "");
    const klasseName = String(row["Klasse"] || "");
    const fachStr = String(row["Fach"] || "");
    const fachKuerzel = fachStr.split(" - ")[0]?.trim();

    const lp = lpName !== "(unbesetzt)" ? lehrpersonen.find((l) => l.name === lpName) : null;
    const kl = klassen.find((k) => k.name === klasseName);
    const fach = faecher.find((f) => f.kuerzel === fachKuerzel);

    const typ = String(row["Typ"] || "Standard") as "Standard" | "HK" | "Praktikum" | "SHT" | "Pool";
    const lektionen = Number(row["Lektionen"] || 0);
    const klasseId = kl?.id || "";
    const entsch = klasseId ? calculateEntschaedigung(klasseId, typ, lektionen, klassen) : { faktor: 0, stunden: 0 };

    return {
      id: generateId("ZT"),
      lpId: lp?.id || null,
      klasseId,
      fachId: fach?.id || "",
      typ,
      lektionen,
      hatHk: row["HK"] === "Ja",
      entschaedigungFaktor: entsch.faktor,
      entschaedigungStunden: entsch.stunden,
      bemerkungen: String(row["Bemerkungen"] || ""),
    };
  });

  return { lehrpersonen, klassen, faecher, zuteilungen, gekoppelt: [] };
}

function readSheet(wb: XLSX.WorkBook, name: string): Record<string, unknown>[] {
  const sheet = wb.Sheets[name];
  if (!sheet) return [];
  return XLSX.utils.sheet_to_json(sheet);
}

function parseDate(value: unknown): string | undefined {
  if (!value) return undefined;
  if (typeof value === "number") {
    // Excel date serial number
    const date = XLSX.SSF.parse_date_code(value);
    if (date) {
      return `${date.y}-${String(date.m).padStart(2, "0")}-${String(date.d).padStart(2, "0")}`;
    }
  }
  const str = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10);
  return undefined;
}
